import React from 'react';
import { Icon } from '../primitives/Icon';

export interface PipelineStage {
  id: string;
  name: string;
  color?: string;
}

interface StagePipelineProps {
  stages: PipelineStage[];
  /** Current stage name (deals store stage by name, not id) */
  currentStage: string;
  onStageChange?: (stageName: string) => void;
  className?: string;
}

export const StagePipeline: React.FC<StagePipelineProps> = ({ stages, currentStage, onStageChange, className = '' }) => {
  const currentIndex = stages.findIndex(s => s.name === currentStage);

  if (stages.length === 0) return null;

  return (
    <div className={`flex items-center gap-0 overflow-x-auto ${className}`}>
      {stages.map((stage, index) => {
        const isActive = stage.name === currentStage;
        const isPassed = currentIndex >= 0 && index < currentIndex;
        const isLast = index === stages.length - 1;

        const circleColor = isActive
          ? (stage.color || '#2563eb')
          : isPassed ? '#10b981' : '#e2e8f0';

        return (
          <React.Fragment key={stage.id}>
            <button
              onClick={() => onStageChange?.(stage.name)}
              className={[
                'relative flex flex-col items-center gap-2 px-3 py-2 rounded-lg transition-colors flex-shrink-0 group',
                onStageChange ? 'cursor-pointer hover:bg-slate-50' : 'cursor-default',
              ].join(' ')}
              style={{ minWidth: '90px' }}
              disabled={!onStageChange}
            >
              <div
                className="size-10 rounded-full flex items-center justify-center font-bold text-sm transition-all border-2 flex-shrink-0"
                style={{
                  background: isActive || isPassed ? circleColor : 'white',
                  color: isActive || isPassed ? 'white' : '#64748b',
                  borderColor: circleColor,
                  boxShadow: isActive ? `0 0 0 4px ${circleColor}22` : undefined,
                }}
              >
                {isPassed ? <Icon name="check" size={18} /> : index + 1}
              </div>
              <span
                className={[
                  'text-[11px] font-semibold whitespace-nowrap',
                  isActive ? 'text-slate-900' : isPassed ? 'text-emerald-700' : 'text-slate-500',
                ].join(' ')}
              >
                {stage.name}
              </span>
            </button>

            {!isLast && (
              <div
                className="flex-1 h-0.5 min-w-[20px] transition-colors self-start mt-5"
                style={{ background: isPassed ? '#10b981' : '#e2e8f0' }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
